import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Input,
  Label,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import Swal from "sweetalert2";
import axios from "axios";

// Acomodo de mercancía por ubicación:
//  - Filtra el inventario de CEDIS por ubicación (default "Pendiente" = lo
//    recibido que aún no se acomoda).
//  - "Mover" manda piezas a otra ubicación. El backend valida la capacidad:
//    rack = 1 tarima (un artículo); rumbas RR/RM/RC hasta 50; Pendiente sin límite.
function AcomodoUbicaciones() {
  const { user } = useContext(AuthContext);
  const URL_INVENTARIOS = process.env.REACT_APP_URL_INVENTARIOS;
  const URL_UBICACIONES = process.env.REACT_APP_URL_UBICACIONES;
  const URL_LOGS = process.env.REACT_APP_URL_LOGS;
  const AREA_CEDIS = process.env.REACT_APP_AREAS_CEDIS;

  const headers = {
    Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
  };

  // Se puede abrir ya filtrado: /AcomodoUbicaciones?ubicacion=F-1-3A (desde el catalogo)
  const [ubicacionFiltro, setUbicacionFiltro] = useState(
    () => new URLSearchParams(window.location.search).get("ubicacion") || "Pendiente"
  );
  const [renglones, setRenglones] = useState([]);
  const [ubicaciones, setUbicaciones] = useState([]); // catálogo + ocupación
  const [search, setSearch] = useState("");
  const [cargando, setCargando] = useState(false);

  // modal mover
  const [modal, setModal] = useState(false);
  const toggle = () => setModal(!modal);
  const [renglonMover, setRenglonMover] = useState(null);
  // Acomodo por tarimas: cada linea es una tarima con su ubicacion y sus piezas
  const nuevaLinea = (cantidad = "") => ({ id: Math.random().toString(36).slice(2), ubicacion: "", cantidad });
  const [lineas, setLineas] = useState([]);
  const [porTarima, setPorTarima] = useState("");
  const [validaBoton, setValidaBoton] = useState(true);

  const cargarUbicaciones = () =>
    axios
      .get(`${URL_UBICACIONES}Ocupacion`, { headers })
      .then((r) => setUbicaciones(r.data || []))
      .catch((e) => console.log(e));

  const cargarRenglones = (ubi) => {
    if (!ubi) return;
    setCargando(true);
    axios
      .get(`${URL_INVENTARIOS}BuscaUbicaciones/${encodeURIComponent(ubi)}`, { headers })
      .then((r) => {
        setRenglones(r.data || []);
        setCargando(false);
      })
      .catch((e) => {
        console.log(e);
        setRenglones([]);
        setCargando(false);
      });
  };

  useEffect(() => {
    cargarUbicaciones();
  }, []);

  useEffect(() => {
    cargarRenglones(ubicacionFiltro);
  }, [ubicacionFiltro]);

  const filtrados = useMemo(() => {
    const s = search.toLowerCase();
    return renglones.filter(
      (r) =>
        !s ||
        (r.codigo || "").toLowerCase().includes(s) ||
        (r.nombre || "").toLowerCase().includes(s)
    );
  }, [renglones, search]);

  const totalPzs = filtrados.reduce((a, r) => a + (Number(r.cantidadAnterior) || 0), 0);

  const ubiPorNombre = useMemo(() => new Map(ubicaciones.map((u) => [u.nombre, u])), [ubicaciones]);
  const destinosLista = useMemo(
    () =>
      ubicaciones
        .filter((u) => !u.llena && u.nombre !== (renglonMover && renglonMover.ubicacion))
        .sort((a, b) => (a.nombre > b.nombre ? 1 : -1)),
    [ubicaciones, renglonMover]
  );

  const etiquetaDestino = (u) => {
    const cap = Number(u.capacidadTarimas) || 0;
    if (cap === 0) return `${u.nombre} · sin límite`;
    if (u.tipo === "Rumba") return `${u.nombre} · rumba ${u.tarimasUsadas}/${cap} tarimas`;
    return `${u.nombre} · ${u.tarimasUsadas > 0 ? "OCUPADA" : "libre"}`;
  };

  function abrirMover(r) {
    setRenglonMover(r);
    setLineas([nuevaLinea()]);
    setPorTarima("");
    setValidaBoton(true);
    toggle();
  }

  const disponible = renglonMover ? Number(renglonMover.cantidadAnterior) || 0 : 0;
  const asignado = lineas.reduce((t, l) => t + (Number(l.cantidad) || 0), 0);
  const restante = disponible - asignado;

  const cambiaLinea = (id, campo, valor) =>
    setLineas((ls) => ls.map((l) => (l.id === id ? { ...l, [campo]: valor } : l)));

  // Reparte lo disponible en tarimas de N piezas (la ultima lleva el sobrante)
  function repartir() {
    const n = Math.floor(Number(porTarima) || 0);
    if (n <= 0) {
      Swal.fire({ icon: "error", title: "Escribe cuantas piezas lleva cada tarima" });
      return;
    }
    const tarimas = Math.ceil(disponible / n);
    if (tarimas > 60) {
      Swal.fire({ icon: "warning", title: `Serian ${tarimas} tarimas`, text: "Revisa las piezas por tarima." });
      return;
    }
    const nuevas = [];
    for (let i = 0; i < tarimas; i++) {
      const previa = lineas[i];
      nuevas.push({ ...(previa || nuevaLinea()), cantidad: Math.min(n, disponible - i * n) });
    }
    setLineas(nuevas);
  }

  // Estado de una ubicacion para esta tarima (rack = una tarima)
  function estadoLinea(l) {
    if (!l.ubicacion) return null;
    const u = ubiPorNombre.get(l.ubicacion.trim());
    if (!u) return { color: "#dc2626", texto: "No existe en el catalogo" };
    if (u.llena) return { color: "#dc2626", texto: "Llena" };
    const cap = Number(u.capacidadTarimas) || 0;
    const veces = lineas.filter((x) => x.ubicacion.trim() === l.ubicacion.trim()).length;
    if (cap === 1 && veces > 1) return { color: "#dc2626", texto: "Rack repetido: es una sola tarima" };
    if (cap === 1 && u.tarimasUsadas > 0) return { color: "#d97706", texto: "Ocupada: solo si es el mismo articulo" };
    if (cap === 0) return { color: "#15803d", texto: "Sin limite" };
    // Rumba: se llena por tarimas (cada renglon es una tarima)
    if (u.tarimasUsadas + veces > cap)
      return { color: "#dc2626", texto: `Rumba llena: ${u.tarimasUsadas}/${cap}, caben ${Math.max(0, cap - u.tarimasUsadas)}` };
    return { color: "#15803d", texto: `Rumba ${u.tarimasUsadas + veces}/${cap} tarimas con esta` };
  }

  function mover() {
    const usadas = lineas.filter((l) => l.ubicacion.trim() || Number(l.cantidad));
    if (!usadas.length) {
      Swal.fire({ icon: "error", title: "Agrega al menos una tarima con ubicacion y cantidad" });
      return;
    }
    for (const [i, l] of usadas.entries()) {
      if (!l.ubicacion.trim()) {
        Swal.fire({ icon: "error", title: `Tarima ${i + 1}: falta la ubicacion` });
        return;
      }
      if (!(Number(l.cantidad) > 0)) {
        Swal.fire({ icon: "error", title: `Tarima ${i + 1}: la cantidad debe ser mayor a 0` });
        return;
      }
      const e = estadoLinea(l);
      if (e && e.color === "#dc2626") {
        Swal.fire({ icon: "error", title: `Tarima ${i + 1} (${l.ubicacion}): ${e.texto}` });
        return;
      }
    }
    const total = usadas.reduce((t, l) => t + Number(l.cantidad), 0);
    if (total > disponible) {
      Swal.fire({
        icon: "error",
        title: "No hay suficiente",
        text: `Repartiste ${total} pzs y en ${renglonMover.ubicacion} solo hay ${disponible}.`,
      });
      return;
    }
    setValidaBoton(false);
    axios
      .patch(
        `${URL_INVENTARIOS}MoverUbicacionVarias`,
        {
          articulos: renglonMover.idArticulo,
          ubicacionOrigen: renglonMover.ubicacion,
          destinos: usadas.map((l) => ({ ubicacion: l.ubicacion.trim(), cantidad: Number(l.cantidad) })),
          areas: AREA_CEDIS,
        },
        { headers }
      )
      .then((res) => {
        const detalle = usadas.map((l) => `${l.ubicacion.trim()}:${l.cantidad}`).join(", ");
        axios.post(
          URL_LOGS,
          {
            tipo: "Mover Ubicación",
            detalle: `${renglonMover.codigo} · ${total} pzs · ${renglonMover.ubicacion} → ${detalle}`,
            user: user.id,
          },
          { headers }
        );
        toggle();
        const quedan = res.data && res.data.quedanEnOrigen;
        Swal.fire(
          "¡Listo!",
          `${total.toLocaleString("en-US")} pzs acomodadas en ${usadas.length} tarima${usadas.length > 1 ? "s" : ""}` +
            (quedan ? `. Quedan ${quedan.toLocaleString("en-US")} en ${renglonMover.ubicacion}.` : "."),
          "success"
        );
        cargarRenglones(ubicacionFiltro);
        cargarUbicaciones();
      })
      .catch((error) => {
        // El backend manda el motivo exacto (ocupada, llena, sin existencia, no existe)
        Swal.fire({
          icon: "warning",
          title: "No se pudo acomodar",
          text: `${error?.response?.data || "Error al mover"}`,
        });
        setValidaBoton(true);
      });
  }


  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_inventarios ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button size="sm" href="/MenuInventarios" className="btn btn-danger">
                Regresar
              </Button>{" "}
              <Button size="sm" color="secondary" href="/CatalogoUbicaciones">
                <i class="fas fa-th"></i> Catálogo de ubicaciones
              </Button>
            </Col>
          </Row>
          <h3 align="center">Acomodo de Mercancía</h3>
          <Row className="align-items-end">
            <Col md={3}>
              <Label className="mr-sm-2">Ubicación</Label>
              <Input
                bsSize="sm"
                type="select"
                value={ubicacionFiltro}
                onChange={(e) => setUbicacionFiltro(e.target.value)}
              >
                <option value="Pendiente">Pendiente (por acomodar)</option>
                {ubicaciones
                  .filter((u) => u.nombre !== "Pendiente" && u.tarimasUsadas > 0)
                  .sort((a, b) => (a.nombre > b.nombre ? 1 : -1))
                  .map((u) => (
                    <option key={u._id} value={u.nombre}>
                      {u.nombre} ({u.tarimasUsadas} art.)
                    </option>
                  ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Buscar artículo</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="SKU o nombre"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={6} className="text-right">
              <span style={{ fontSize: 13, color: "#64748b" }}>
                {filtrados.length} artículo(s) · {totalPzs.toLocaleString("en-US")} pzs en{" "}
                <b>{ubicacionFiltro}</b>
              </span>
            </Col>
          </Row>
          <br />
          {cargando ? (
            <h5 align="center" style={{ color: "#64748b" }}>Cargando…</h5>
          ) : filtrados.length === 0 ? (
            <h5 align="center" style={{ color: "#64748b" }}>
              No hay artículos en {ubicacionFiltro}
            </h5>
          ) : (
            <Table size="sm" striped borderless className="table-responsive-xl">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Artículo</th>
                  <th>Ubicación</th>
                  <th className="text-right">Piezas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtrados.map((r) => (
                  <tr key={r._id}>
                    <td>{r.codigo}</td>
                    <td>{r.nombre}</td>
                    <td>
                      <Badge color={r.ubicacion === "Pendiente" ? "warning" : "info"} pill>
                        {r.ubicacion}
                      </Badge>
                    </td>
                    <td className="text-right">
                      {Number(r.cantidadAnterior).toLocaleString("en-US")}
                    </td>
                    <td>
                      <Button size="sm" color="primary" onClick={() => abrirMover(r)}>
                        <i class="fas fa-dolly"></i> Mover
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      ) : undefined}

      <Modal size="lg" isOpen={modal} toggle={toggle}>
        <ModalHeader toggle={toggle}>Acomodar por tarimas</ModalHeader>
        <ModalBody>
          {renglonMover ? (
            <>
              <div style={{ marginBottom: 10 }}>
                <b>{renglonMover.codigo}</b> — {renglonMover.nombre}
                <br />
                <small style={{ color: "#64748b" }}>
                  Desde <b>{renglonMover.ubicacion}</b> · disponible:{" "}
                  {Number(renglonMover.cantidadAnterior).toLocaleString("en-US")} pzs
                </small>
              </div>
              <Row className="align-items-end" style={{ marginBottom: 8 }}>
                <Col md={5}>
                  <Label style={{ fontSize: 12 }}>Piezas por tarima</Label>
                  <Input
                    bsSize="sm"
                    type="number"
                    min="1"
                    placeholder="Ej. 120"
                    value={porTarima}
                    onChange={(e) => setPorTarima(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && repartir()}
                  />
                </Col>
                <Col md={7}>
                  <Button
                    size="sm"
                    color="info"
                    title="Arma las tarimas con esas piezas cada una; solo falta escribir la ubicacion de cada una"
                    onClick={repartir}
                  >
                    <i class="fas fa-layer-group"></i> Repartir en tarimas
                  </Button>
                </Col>
              </Row>
              <Table size="sm" borderless style={{ marginBottom: 4 }}>
                <thead>
                  <tr>
                    <th style={{ fontSize: 12, width: 30 }}>#</th>
                    <th style={{ fontSize: 12 }}>Ubicacion</th>
                    <th style={{ fontSize: 12, width: 110 }}>Piezas</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((l, i) => {
                    const e = estadoLinea(l);
                    return (
                      <tr key={l.id}>
                        <td style={{ fontSize: 12, color: "#64748b", paddingTop: 8 }}>{i + 1}</td>
                        <td>
                          <Input
                            bsSize="sm"
                            list="acomodo-destinos"
                            placeholder="Ej. A-1, RR0, B-12-3B…"
                            value={l.ubicacion}
                            onChange={(ev) => cambiaLinea(l.id, "ubicacion", ev.target.value.toUpperCase())}
                          />
                          {e ? <div style={{ fontSize: 11, color: e.color }}>{e.texto}</div> : null}
                        </td>
                        <td>
                          <Input
                            bsSize="sm"
                            type="number"
                            min="1"
                            value={l.cantidad}
                            onChange={(ev) => cambiaLinea(l.id, "cantidad", ev.target.value)}
                          />
                        </td>
                        <td>
                          <Button
                            size="sm"
                            color="danger"
                            title="Quitar esta tarima"
                            disabled={lineas.length === 1}
                            onClick={() => setLineas((ls) => ls.filter((x) => x.id !== l.id))}
                          >
                            <i class="fas fa-times"></i>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <datalist id="acomodo-destinos">
                {destinosLista.map((u) => (
                  <option key={u._id} value={u.nombre}>
                    {etiquetaDestino(u)}
                  </option>
                ))}
              </datalist>
              <Button
                size="sm"
                color="secondary"
                outline
                title="Agregar otra tarima"
                onClick={() => setLineas((ls) => [...ls, nuevaLinea(restante > 0 ? restante : "")])}
              >
                <i class="fas fa-plus"></i> Agregar tarima
              </Button>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 13,
                  color: restante < 0 ? "#dc2626" : restante === 0 ? "#15803d" : "#64748b",
                }}
              >
                Asignado {asignado.toLocaleString("en-US")} de {disponible.toLocaleString("en-US")} pzs
                {restante > 0 ? ` · quedan ${restante.toLocaleString("en-US")} en ${renglonMover.ubicacion}` : ""}
                {restante < 0 ? ` · te pasaste por ${(-restante).toLocaleString("en-US")}` : ""}
              </div>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={mover}
            disabled={!validaBoton || asignado <= 0 || restante < 0}
            title="Acomodar las tarimas en sus ubicaciones"
          >
            Acomodar {lineas.length > 1 ? `${lineas.length} tarimas` : ""}
          </Button>
          <Button color="secondary" onClick={toggle}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default AcomodoUbicaciones;
